import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ApolloError } from "@apollo/client"

import { getSalonStatus } from "./datasourceConfigUtils"

import { getExtensionsCode } from "@/lib/ts/graphql"
import {
	useSouhlAdminClient,
	useSouhlAdminMutation,
	useSouhlAdminQuery,
} from "@/provider/apollo/Apollo"
import { useErrorNotification } from "@/souhl_admin/components/ui/ErrorNotification/ErrorNotification"
import { getFragmentData, graphql } from "@/souhl_admin/gql/__generated__"
import type {
	DataSource,
	DataSourcePolicy,
} from "@/souhl_admin/gql/__generated__/graphql"

const PASSWORD_CLIPBOARD_CLEAR_DELAY_MS = 30_000

const passwordCopyKey = (salonId: number, dataSource: DataSource) =>
	`${salonId}:${dataSource}`

const getGraphQLErrorCode = (error: unknown): string | undefined => {
	if (!(error instanceof ApolloError)) return undefined
	const graphQLError = error.graphQLErrors[0]
	if (!graphQLError) return undefined
	return getExtensionsCode(graphQLError)
}

const salonStatusFragment = graphql(/* GraphQL */ `
	fragment SalonStatusForDatasourceConfig on Salon {
		id
		dataSourcePolicy
		goalDataSourcePolicy
		dataSourceConnectionStatuses {
			hasIdPassError
		}
	}
`)

const getCompanyForDatasourceConfigQuery = graphql(/* GraphQL */ `
	query GetCompanyForDatasourceConfig($param: CompanyParam!) {
		company(param: $param) {
			company {
				id
				salons {
					...SalonStatusForDatasourceConfig
					...DatasourceConfigTable
				}
			}
		}
	}
`)

const getDataSourceColumnsQuery = graphql(/* GraphQL */ `
	query GetDataSourceColumns($param: DataSourceColumnsParam!) {
		dataSourceColumns(param: $param) {
			dataSourceColumns {
				dataSource
				label
			}
		}
	}
`)

const getDataSourcePolicyOptionsQuery = graphql(/* GraphQL */ `
	query GetDataSourcePolicyOptions($param: DataSourcePolicyOptionsParam!) {
		dataSourcePolicyOptions(param: $param) {
			value
			label
		}
	}
`)

const updateSalonGoalDataSourcePoliciesMutation = graphql(/* GraphQL */ `
	mutation UpdateSalonGoalDataSourcePolicies(
		$input: UpdateSalonGoalDataSourcePoliciesInput!
	) {
		updateSalonGoalDataSourcePolicies(input: $input) {
			success
		}
	}
`)

const updateSalonDataSourcePolicyMemoMutation = graphql(/* GraphQL */ `
	mutation UpdateSalonDataSourcePolicyMemo(
		$input: UpdateSalonDataSourcePolicyMemoInput!
	) {
		updateSalonDataSourcePolicyMemo(input: $input) {
			salon {
				id
				dataSourcePolicyMemo
			}
		}
	}
`)

const getDataSourceLoginPasswordMutation = graphql(/* GraphQL */ `
	mutation GetDataSourceLoginPassword(
		$input: GetDataSourceLoginPasswordInput!
	) {
		getDataSourceLoginPassword(input: $input) {
			password
		}
	}
`)

export function useDatasourceConfig(companyId: string) {
	const { addError, addInfo } = useErrorNotification()
	const souhlAdminClient = useSouhlAdminClient()
	const [selectedSalonIds, setSelectedSalonIds] = useState<Set<number>>(
		new Set()
	)
	const [copyingPasswordKeys, setCopyingPasswordKeys] = useState<Set<string>>(
		new Set()
	)
	const copyingPasswordKeysRef = useRef<Set<string>>(new Set())
	const clipboardClearTimeoutRef = useRef<number | null>(null)
	const clipboardClearFocusListenerRef = useRef<(() => void) | null>(null)

	const clearScheduledClipboardClear = useCallback(() => {
		if (clipboardClearTimeoutRef.current !== null) {
			window.clearTimeout(clipboardClearTimeoutRef.current)
			clipboardClearTimeoutRef.current = null
		}
		if (clipboardClearFocusListenerRef.current !== null) {
			window.removeEventListener(
				"focus",
				clipboardClearFocusListenerRef.current
			)
			clipboardClearFocusListenerRef.current = null
		}
	}, [])

	const scheduleClipboardClear = useCallback(() => {
		clearScheduledClipboardClear()

		clipboardClearTimeoutRef.current = window.setTimeout(() => {
			clipboardClearTimeoutRef.current = null

			const clearClipboard = () => {
				clipboardClearFocusListenerRef.current = null
				void navigator.clipboard.writeText("").catch(() => undefined)
			}

			if (document.visibilityState === "visible" && document.hasFocus()) {
				clearClipboard()
				return
			}

			clipboardClearFocusListenerRef.current = clearClipboard
			window.addEventListener("focus", clearClipboard, { once: true })
		}, PASSWORD_CLIPBOARD_CLEAR_DELAY_MS)
	}, [clearScheduledClipboardClear])

	useEffect(() => clearScheduledClipboardClear, [clearScheduledClipboardClear])

	const {
		data: companyData,
		loading: companyLoading,
		refetch: refetchCompany,
	} = useSouhlAdminQuery(getCompanyForDatasourceConfigQuery, {
		variables: { param: { id: companyId } },
		fetchPolicy: "network-only",
	})

	const { data: columnsData, loading: columnsLoading } = useSouhlAdminQuery(
		getDataSourceColumnsQuery,
		{ variables: { param: { companyId } }, fetchPolicy: "cache-first" }
	)

	const { data: policyOptionsData } = useSouhlAdminQuery(
		getDataSourcePolicyOptionsQuery,
		{ variables: { param: {} }, fetchPolicy: "cache-first" }
	)

	const [updateGoalPolicies, { loading: updatingGoal }] = useSouhlAdminMutation(
		updateSalonGoalDataSourcePoliciesMutation
	)
	const [updateMemo] = useSouhlAdminMutation(
		updateSalonDataSourcePolicyMemoMutation
	)

	const salonsRaw = useMemo(
		() => companyData?.company?.company?.salons ?? [],
		[companyData]
	)
	const salonsForStatus = useMemo(
		() => salonsRaw.map((raw) => getFragmentData(salonStatusFragment, raw)),
		[salonsRaw]
	)
	const dataSourceColumns =
		columnsData?.dataSourceColumns?.dataSourceColumns ?? []
	const policyOptions = policyOptionsData?.dataSourcePolicyOptions ?? []

	const statusCounts = useMemo(() => {
		let match = 0
		let diff = 0
		let unset = 0
		let error = 0
		for (const salon of salonsForStatus) {
			const st = getSalonStatus(
				salon.dataSourcePolicy,
				salon.goalDataSourcePolicy
			)
			if (st === "match") match++
			if (st === "diff") diff++
			if (st === "unset") unset++
			if (salon.dataSourceConnectionStatuses.some((s) => s.hasIdPassError))
				error++
		}
		return { match, diff, unset, error }
	}, [salonsForStatus])

	const handleSelectAll = useCallback(
		(checked: boolean) => {
			setSelectedSalonIds(
				checked ? new Set(salonsForStatus.map((s) => s.id)) : new Set()
			)
		},
		[salonsForStatus]
	)

	const handleSelectSalon = useCallback((salonId: number, checked: boolean) => {
		setSelectedSalonIds((prev) => {
			const next = new Set(prev)
			if (checked) {
				next.add(salonId)
			} else {
				next.delete(salonId)
			}
			return next
		})
	}, [])

	const handleBulkApply = useCallback(
		async (goalPolicy: DataSourcePolicy | null) => {
			if (selectedSalonIds.size === 0) return

			try {
				await updateGoalPolicies({
					variables: {
						input: {
							salonIds: Array.from(selectedSalonIds),
							goalDataSourcePolicy: goalPolicy,
						},
					},
				})
				setSelectedSalonIds(new Set())
				await refetchCompany()
			} catch (err: unknown) {
				console.error("Failed to bulk apply goal policies:", err)
				addError("目標の媒体設定の一括適用に失敗しました")
				throw err
			}
		},
		[selectedSalonIds, updateGoalPolicies, refetchCompany, addError]
	)

	const handleUpdateMemo = useCallback(
		async (salonId: number, memo: string | null) => {
			try {
				await updateMemo({ variables: { input: { salonId, memo } } })
			} catch (err: unknown) {
				console.error("Failed to update memo:", err)
				addError("メモの更新に失敗しました")
				throw err
			}
		},
		[updateMemo, addError]
	)

	const isPasswordCopying = useCallback(
		(salonId: number, dataSource: DataSource) =>
			copyingPasswordKeys.has(passwordCopyKey(salonId, dataSource)),
		[copyingPasswordKeys]
	)

	const handleCopyPassword = useCallback(
		async (salonId: number, dataSource: DataSource) => {
			const key = passwordCopyKey(salonId, dataSource)
			if (copyingPasswordKeysRef.current.has(key)) return

			if (!navigator.clipboard?.writeText) {
				addError("このブラウザではクリップボードへコピーできません")
				return
			}

			const dataSourceLabel =
				dataSourceColumns.find((col) => col.dataSource === dataSource)?.label ??
				dataSource

			setCopyingPasswordKeys((prev) => new Set(prev).add(key))
			copyingPasswordKeysRef.current.add(key)
			try {
				const result = await souhlAdminClient.mutate({
					mutation: getDataSourceLoginPasswordMutation,
					variables: { input: { salonId, dataSource } },
					fetchPolicy: "no-cache",
					context: { disableErrorDialog: true },
				})
				const password = result.data?.getDataSourceLoginPassword.password
				if (!password) {
					addError(`${dataSourceLabel}のパスワードを取得できませんでした`)
					return
				}

				try {
					await navigator.clipboard.writeText(password)
				} catch {
					addError("クリップボードへのコピーに失敗しました")
					return
				}

				scheduleClipboardClear()
				addInfo(
					`${dataSourceLabel} のパスワードをクリップボードにコピーしました`
				)
			} catch (err: unknown) {
				const code = getGraphQLErrorCode(err)
				if (code === "NOT_FOUND") {
					addError("パスワードが Parameter Store に登録されていません")
					return
				}
				if (code === "PERMISSION_DENIED") {
					addError("パスワードの取得権限がありません")
					return
				}

				console.error("Failed to copy data source login password:", err)
				addError("パスワードの取得に失敗しました")
			} finally {
				copyingPasswordKeysRef.current.delete(key)
				setCopyingPasswordKeys((prev) => {
					const next = new Set(prev)
					next.delete(key)
					return next
				})
			}
		},
		[
			addError,
			addInfo,
			dataSourceColumns,
			scheduleClipboardClear,
			souhlAdminClient,
		]
	)

	return {
		salons: salonsRaw,
		dataSourceColumns,
		policyOptions,
		statusCounts,
		selectedSalonIds,
		companyLoading,
		columnsLoading,
		updatingGoal,
		handleSelectAll,
		handleSelectSalon,
		handleBulkApply,
		handleUpdateMemo,
		handleCopyPassword,
		isPasswordCopying,
	}
}
